import { cn } from "@/lib/utils";
import { REGION_NAMES, Region } from "@/lib/regions";
import { Globe } from "lucide-react";

export interface RegionBadgeProps {
  region?: string | null;
  longName?: boolean;
  className?: string;
  showFlag?: boolean;
}

export const REGION_FLAGS: Record<string, string> = {
  EUW: "EUW",
  EUW1: "EUW",
  NA: "NA",
  NA1: "NA",
  KR: "KR",
  KR1: "KR",
  KOR: "KR",
  EUNE: "EUNE",
  EUN1: "EUNE",
  EUN: "EUNE",
  BR: "BR",
  BR1: "BR",
  BRA: "BR",
  JP: "JP",
  JP1: "JP",
  JPN: "JP",
  LAN: "LAN",
  LA1: "LAN",
  LAS: "LAS",
  LA2: "LAS",
  OCE: "OCE",
  OC1: "OCE",
  OCE1: "OCE",
  RU: "RU",
  RUS: "RU",
  TR: "TR",
  TR1: "TR",
  TUR: "TR",
  TW2: "TW",
  CHN: "CHN",
  ME: "ME",
  ME1: "ME",
  MEA: "ME",
};

const REGION_STYLES: Record<string, {
  border: string;
  bg: string;
  text: string;
  glow: string;
}> = {
  ME: {
    border: "border-emerald-500/40 hover:border-emerald-400/80",
    bg: "bg-emerald-500/10",
    text: "text-emerald-300",
    glow: "shadow-[0_0_12px_rgba(16,185,129,0.3)]",
  },
  ME1: {
    border: "border-emerald-500/40 hover:border-emerald-400/80",
    bg: "bg-emerald-500/10",
    text: "text-emerald-300",
    glow: "shadow-[0_0_12px_rgba(16,185,129,0.3)]",
  },
  EUW: {
    border: "border-blue-500/40 hover:border-blue-400/80",
    bg: "bg-blue-500/10",
    text: "text-blue-300",
    glow: "shadow-[0_0_12px_rgba(59,130,246,0.3)]",
  },
  NA: {
    border: "border-rose-500/40 hover:border-rose-400/80",
    bg: "bg-rose-500/10",
    text: "text-rose-300",
    glow: "shadow-[0_0_12px_rgba(244,63,94,0.3)]",
  },
  KR: {
    border: "border-amber-400/40 hover:border-amber-400/80",
    bg: "bg-amber-400/10",
    text: "text-amber-300",
    glow: "shadow-[0_0_12px_rgba(251,191,36,0.3)]",
  },
  KOR: {
    border: "border-amber-400/40 hover:border-amber-400/80",
    bg: "bg-amber-400/10",
    text: "text-amber-300",
    glow: "shadow-[0_0_12px_rgba(251,191,36,0.3)]",
  },
  EUNE: {
    border: "border-orange-500/40 hover:border-orange-400/80",
    bg: "bg-orange-500/10",
    text: "text-orange-300",
    glow: "shadow-[0_0_12px_rgba(249,115,22,0.3)]",
  },
  EUN: {
    border: "border-orange-500/40 hover:border-orange-400/80",
    bg: "bg-orange-500/10",
    text: "text-orange-300",
    glow: "shadow-[0_0_12px_rgba(249,115,22,0.3)]",
  },
  BR: {
    border: "border-emerald-500/40 hover:border-emerald-400/80",
    bg: "bg-emerald-500/10",
    text: "text-emerald-300",
    glow: "shadow-[0_0_12px_rgba(16,185,129,0.3)]",
  },
  BRA: {
    border: "border-emerald-500/40 hover:border-emerald-400/80",
    bg: "bg-emerald-500/10",
    text: "text-emerald-300",
    glow: "shadow-[0_0_12px_rgba(16,185,129,0.3)]",
  },
  JP: {
    border: "border-red-400/40 hover:border-red-300/80",
    bg: "bg-red-400/10",
    text: "text-red-200",
    glow: "shadow-[0_0_12px_rgba(248,113,113,0.3)]",
  },
  JPN: {
    border: "border-red-400/40 hover:border-red-300/80",
    bg: "bg-red-400/10",
    text: "text-red-200",
    glow: "shadow-[0_0_12px_rgba(248,113,113,0.3)]",
  },
  LAN: {
    border: "border-purple-500/40 hover:border-purple-400/80",
    bg: "bg-purple-500/10",
    text: "text-purple-300",
    glow: "shadow-[0_0_12px_rgba(168,85,247,0.3)]",
  },
  LA1: {
    border: "border-purple-500/40 hover:border-purple-400/80",
    bg: "bg-purple-500/10",
    text: "text-purple-300",
    glow: "shadow-[0_0_12px_rgba(168,85,247,0.3)]",
  },
  LAS: {
    border: "border-pink-500/40 hover:border-pink-400/80",
    bg: "bg-pink-500/10",
    text: "text-pink-300",
    glow: "shadow-[0_0_12px_rgba(236,72,153,0.3)]",
  },
  LA2: {
    border: "border-pink-500/40 hover:border-pink-400/80",
    bg: "bg-pink-500/10",
    text: "text-pink-300",
    glow: "shadow-[0_0_12px_rgba(236,72,153,0.3)]",
  },
  OCE: {
    border: "border-cyan-500/40 hover:border-cyan-400/80",
    bg: "bg-cyan-500/10",
    text: "text-cyan-300",
    glow: "shadow-[0_0_12px_rgba(6,182,212,0.3)]",
  },
  OC1: {
    border: "border-cyan-500/40 hover:border-cyan-400/80",
    bg: "bg-cyan-500/10",
    text: "text-cyan-300",
    glow: "shadow-[0_0_12px_rgba(6,182,212,0.3)]",
  },
  RU: {
    border: "border-slate-400/40 hover:border-slate-300/80",
    bg: "bg-slate-500/10",
    text: "text-slate-200",
    glow: "shadow-[0_0_12px_rgba(148,163,184,0.3)]",
  },
  RUS: {
    border: "border-slate-400/40 hover:border-slate-300/80",
    bg: "bg-slate-500/10",
    text: "text-slate-200",
    glow: "shadow-[0_0_12px_rgba(148,163,184,0.3)]",
  },
  TR: {
    border: "border-teal-400/40 hover:border-teal-300/80",
    bg: "bg-teal-400/10",
    text: "text-teal-300",
    glow: "shadow-[0_0_12px_rgba(45,212,191,0.3)]",
  },
  TUR: {
    border: "border-teal-400/40 hover:border-teal-300/80",
    bg: "bg-teal-400/10",
    text: "text-teal-300",
    glow: "shadow-[0_0_12px_rgba(45,212,191,0.3)]",
  },
  TW2: {
    border: "border-fuchsia-500/40 hover:border-fuchsia-400/80",
    bg: "bg-fuchsia-500/10",
    text: "text-fuchsia-300",
    glow: "shadow-[0_0_12px_rgba(217,70,239,0.3)]",
  },
  CHN: {
    border: "border-noway-mint/40 hover:border-noway-mint/80",
    bg: "bg-noway-mint/10",
    text: "text-noway-mint",
    glow: "shadow-[0_0_12px_rgba(0,245,184,0.3)]",
  },
};

const defaultStyle = {
  border: "border-white/20 hover:border-white/40",
  bg: "bg-white/5",
  text: "text-white/80",
  glow: "shadow-[0_0_8px_rgba(255,255,255,0.15)]",
};

export const RegionBadge = ({
  region,
  longName = false,
  className,
  showFlag = true,
}: RegionBadgeProps) => {
  if (!region) return null;

  const key = region.toUpperCase();
  const style = REGION_STYLES[key] || defaultStyle;
  const regionName = longName ? REGION_NAMES[region as Region] || region : region;

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full font-semibold border backdrop-blur-md transition-all duration-200 hover:scale-105 select-none",
        longName
          ? "px-3 py-1 text-xs tracking-wide"
          : "px-2.5 py-0.5 text-[11px] tracking-wider uppercase",
        style.bg,
        style.text,
        style.border,
        style.glow,
        className
      )}
    >
      {showFlag && (
        <Globe className="w-3 h-3 opacity-80 shrink-0" aria-hidden="true" />
      )}
      <span>{regionName}</span>
    </span>
  );
};
